import { useTranslation } from 'react-i18next';

import { CustomModal } from 'components/shared';
import { CustomModalProps } from 'components/shared/CustomModal/CustomModal';
import { useErrorDescriptions } from 'constants/errorDescriptions';
import { useModal } from 'hooks/useModal';
import { OrderDetailsDTO } from 'interfaces';
import { HTTPService } from 'service';
import { logNetworkError } from 'utils/logNetworkError';

import styles from './CancelOrderModal.module.css';

type CancelOrderModalProps = CustomModalProps & {
   selectedOrderId: string;
   onSuccessCancelOrder: (updatedOrder: OrderDetailsDTO) => void;
};

const CancelOrderModal = ({
   selectedOrderId,
   onSuccessCancelOrder,
   onClose,
   ...props
}: CancelOrderModalProps) => {
   const { t } = useTranslation();

   const { isLoading, errorInfo, setIsLoading, setErrorInfo, closeModalHandler } = useModal({
      onClose,
   });

   const { DEFAULT_ERROR_DESCRIPTIONS } = useErrorDescriptions();

   const handleCancelOrder = () => {
      setIsLoading(true);
      HTTPService.changeOrderStatus(selectedOrderId, 'CANCEL')
         .then(response => {
            onSuccessCancelOrder(response.data);
            closeModalHandler();
         })
         .catch(error => {
            setErrorInfo(DEFAULT_ERROR_DESCRIPTIONS.UNKNOWN);
            logNetworkError(error);
         })
         .finally(() => setIsLoading(false));
   };

   return (
      <CustomModal
         {...props}
         icon="ALERT_TRIANGLE"
         innerTitle={t('order_details_page.cancel_order')}
         error={errorInfo}
         modalContentClassName={styles.modalContent}
         isLoading={isLoading}
         onClose={closeModalHandler}
         primaryButtonProps={{
            text: t('order_details_page.cancel_order'),
            variant: 'filled-danger',
            onClick: handleCancelOrder,
         }}
         secondaryButtonProps={{
            text: t('cancel_pallet_report_modal.back'),
            variant: 'outlined-danger',
            onClick: closeModalHandler,
         }}
      >
         <div className={styles.descriptionContainer}>
            <p className={styles.plainText}>{t('cancel_order_modal.confirmation')}</p>
            <p className={styles.boldBlackText}>{t('cancel_pallet_report_modal.irreversible')}</p>
            <p className={styles.boldRedText}>{t('cancel_order_modal.warning_invoice')}</p>
         </div>
      </CustomModal>
   );
};

export default CancelOrderModal;
